import { cva, type VariantProps } from 'class-variance-authority'
import { motion } from 'framer-motion'
import type { ReactNode } from 'react'

import { RadioGroup, RadioGroupItem } from '@/customer/components/ui/radio-group'
import { cn } from '@/customer/lib/utils'

/**
 * Per-option cva - tints the hover + checked backgrounds for each brand colour.
 */
const checkedBg = cva('', {
  variants: {
    color: {
      yellow: 'hover:bg-yellow/50 data-checked:bg-yellow',
      blue: 'hover:bg-blue/50 data-checked:bg-blue',
      pink: 'hover:bg-pink/50 data-checked:bg-pink',
      'baby-blue': 'hover:bg-baby-blue/30 data-checked:bg-baby-blue',
      green: 'hover:bg-green/50 data-checked:bg-green/90',
      orange: 'hover:bg-orange/50 data-checked:bg-orange/80',
      destructive: 'hover:bg-destructive/50 data-checked:bg-destructive',
      neutral: 'hover:bg-neutral-100 data-checked:bg-neutral-300',
    },
  },
  defaultVariants: { color: 'yellow' },
})

export type CardRadioColor = NonNullable<VariantProps<typeof checkedBg>['color']>

export type CardRadioOption<T extends string> = {
  value: T
  label: string
  /** Optional icon rendered above the label. When omitted, the label sits alone in the tile. */
  icon?: ReactNode
  color?: CardRadioColor
}

type CardRadioGroupProps<T extends string> = {
  value: T | null
  onValueChange: (value: T) => void
  options: readonly CardRadioOption<T>[]
  className?: string
  'aria-labelledby'?: string
}

/**
 * Card-style radio group with an icon + label per option. The icon bounces
 * briefly when its option becomes selected. Used for choose-one pickers where
 * we want a visual, chunky feel (species picker, sign-in method picker, etc.).
 *
 * Each option can specify its own `color` to control the selected-state
 * background - defaults to `pink`.
 */
export function CardRadioGroup<T extends string>({
  value,
  onValueChange,
  options,
  className,
  'aria-labelledby': ariaLabelledBy,
}: CardRadioGroupProps<T>) {
  return (
    <RadioGroup
      value={value}
      onValueChange={(v) => onValueChange(v as T)}
      // Flex + `flex-1` on the items so any number of options shares the
      // row equally and shrinks to fit narrow viewports, rather than
      // wrapping into an uneven grid when there are 3+ options.
      className={cn('flex gap-4', className)}
      aria-labelledby={ariaLabelledBy}
    >
      {options.map(({ value: itemValue, label, icon, color }) => {
        const isSelected = value === itemValue
        return (
          <RadioGroupItem
            key={itemValue}
            value={itemValue}
            className={cn(
              'flex aspect-auto size-auto min-w-0 flex-1 cursor-pointer flex-col items-center justify-center gap-2 rounded-lg border-2 border-input bg-background p-4 text-muted-foreground transition-colors',
              'hover:border-foreground hover:text-foreground',
              'data-checked:border-foreground data-checked:text-foreground',
              checkedBg({ color })
            )}
          >
            {/* Icon bounces when this option becomes selected. `initial={false}`
                avoids a bounce on mount for the default-selected option. Omitted
                entirely when the option has no icon, so the label centres in the tile. */}
            {icon ? (
              <motion.div
                initial={false}
                animate={isSelected ? 'selected' : 'idle'}
                variants={{
                  idle: { scale: 1 },
                  selected: { scale: [1, 1.2, 0.9, 1.08, 1] },
                }}
                transition={{ duration: 0.45, ease: 'easeOut' }}
              >
                {icon}
              </motion.div>
            ) : null}
            <span className="text-label-md">{label}</span>
          </RadioGroupItem>
        )
      })}
    </RadioGroup>
  )
}
